import React, { useState, useCallback, useEffect, useMemo } from 'react';
import {
  CheckCircleIcon,
  ClockIcon,
  StopIcon,
  ExclamationTriangleIcon,
  ChartBarIcon,
  DocumentTextIcon,
  CloudArrowUpIcon,
  ShieldCheckIcon,
  SparklesIcon,
  EyeIcon,
  EyeSlashIcon,
  SignalIcon
} from '@heroicons/react/24/outline';
import LoadingSpinner from '../../common/LoadingSpinner';
import Button from '../../common/Button';
import { useKGImportProgressContext, ProgressStep } from '../../../contexts/KGImportProgressContext';
import { useMediaQuery } from '../../../hooks/useMediaQuery';
import { useAdminTranslation } from '../../../modules/i18n';
import { KG_PROGRESS_MESSAGES, KG_ACTIONS, KG_METRICS_LABELS, KG_IMPORT_STEPS } from '../../../constants/kgMessages';

// Enhanced import progress tracking component for Finbot KG imports
// Provides detailed 8-step workflow visualization with real-time updates

interface ImportProgressStep {
  id: string;
  name: string;
  nameVn: string;
  description: string;
  icon: React.ReactNode;
  estimatedDuration: number; // in seconds
}

interface KGImportProgressTrackerProps {
  jobId: string;
  className?: string;
}

// Define the KG import workflow steps (matching actual backend saga steps)
const IMPORT_STEPS: ImportProgressStep[] = [
  {
    id: KG_IMPORT_STEPS.validatingInputs.id,
    name: KG_IMPORT_STEPS.validatingInputs.name,
    nameVn: KG_IMPORT_STEPS.validatingInputs.nameVn,
    description: KG_IMPORT_STEPS.validatingInputs.description,
    icon: <ShieldCheckIcon className='h-5 w-5' />,
    estimatedDuration: 30
  },
  {
    id: KG_IMPORT_STEPS.backupExisting.id,
    name: KG_IMPORT_STEPS.backupExisting.name,
    nameVn: KG_IMPORT_STEPS.backupExisting.nameVn,
    description: KG_IMPORT_STEPS.backupExisting.description,
    icon: <CloudArrowUpIcon className='h-5 w-5' />,
    estimatedDuration: 60
  },
  {
    id: KG_IMPORT_STEPS.clearExisting.id,
    name: KG_IMPORT_STEPS.clearExisting.name,
    nameVn: KG_IMPORT_STEPS.clearExisting.nameVn,
    description: KG_IMPORT_STEPS.clearExisting.description,
    icon: <ClockIcon className='h-5 w-5' />,
    estimatedDuration: 30
  },
  {
    id: KG_IMPORT_STEPS.importNeo4j.id,
    name: KG_IMPORT_STEPS.importNeo4j.name,
    nameVn: KG_IMPORT_STEPS.importNeo4j.nameVn,
    description: KG_IMPORT_STEPS.importNeo4j.description,
    icon: <ChartBarIcon className='h-5 w-5' />,
    estimatedDuration: 1200
  },
  {
    id: KG_IMPORT_STEPS.importEmbeddings.id,
    name: KG_IMPORT_STEPS.importEmbeddings.name,
    nameVn: KG_IMPORT_STEPS.importEmbeddings.nameVn,
    description: KG_IMPORT_STEPS.importEmbeddings.description,
    icon: <SparklesIcon className='h-5 w-5' />,
    estimatedDuration: 600
  },
  {
    id: KG_IMPORT_STEPS.createTracking.id,
    name: KG_IMPORT_STEPS.createTracking.name,
    nameVn: KG_IMPORT_STEPS.createTracking.nameVn,
    description: KG_IMPORT_STEPS.createTracking.description,
    icon: <DocumentTextIcon className='h-5 w-5' />,
    estimatedDuration: 15
  },
  {
    id: KG_IMPORT_STEPS.linkDocuments.id,
    name: KG_IMPORT_STEPS.linkDocuments.name,
    nameVn: KG_IMPORT_STEPS.linkDocuments.nameVn,
    description: KG_IMPORT_STEPS.linkDocuments.description,
    icon: <CheckCircleIcon className='h-5 w-5' />,
    estimatedDuration: 60
  },
  {
    id: KG_IMPORT_STEPS.complete.id,
    name: KG_IMPORT_STEPS.complete.name,
    nameVn: KG_IMPORT_STEPS.complete.nameVn,
    description: KG_IMPORT_STEPS.complete.description,
    icon: <CheckCircleIcon className='h-5 w-5' />,
    estimatedDuration: 10
  }
];

export const KGImportProgressTracker: React.FC<KGImportProgressTrackerProps> = ({ jobId, className = '' }) => {
  // Enhanced state for mobile and accessibility
  const [showDetails, setShowDetails] = useState(true);
  const [isCompact, setIsCompact] = useState(false);

  // Translation hook
  const { t } = useAdminTranslation();

  // Helper function to get translated step name and description
  const getStepTranslation = (stepId: string) => {
    const stepKeyMap: Record<string, { name: string; desc: string }> = {
      validate_data: {
        name: 'content.knowledgeGraph.progressTracker.steps.validateData',
        desc: 'content.knowledgeGraph.progressTracker.steps.validateDataDesc'
      },
      backup_existing: {
        name: 'content.knowledgeGraph.progressTracker.steps.backupExisting',
        desc: 'content.knowledgeGraph.progressTracker.steps.backupExistingDesc'
      },
      clear_existing: {
        name: 'content.knowledgeGraph.progressTracker.steps.clearExisting',
        desc: 'content.knowledgeGraph.progressTracker.steps.clearExistingDesc'
      },
      import_neo4j_data: {
        name: 'content.knowledgeGraph.progressTracker.steps.importNeo4j',
        desc: 'content.knowledgeGraph.progressTracker.steps.importNeo4jDesc'
      },
      import_embeddings: {
        name: 'content.knowledgeGraph.progressTracker.steps.importEmbeddings',
        desc: 'content.knowledgeGraph.progressTracker.steps.importEmbeddingsDesc'
      },
      create_tracking: {
        name: 'content.knowledgeGraph.progressTracker.steps.createTracking',
        desc: 'content.knowledgeGraph.progressTracker.steps.createTrackingDesc'
      },
      link_documents: {
        name: 'content.knowledgeGraph.progressTracker.steps.linkDocuments',
        desc: 'content.knowledgeGraph.progressTracker.steps.linkDocumentsDesc'
      },
      complete: {
        name: 'content.knowledgeGraph.progressTracker.steps.complete',
        desc: 'content.knowledgeGraph.progressTracker.steps.completeDesc'
      }
    };

    return stepKeyMap[stepId] || { name: stepId, desc: '' };
  };

  // Responsive design hooks
  const isMobile = useMediaQuery('(max-width: 768px)');
  const isSmallMobile = useMediaQuery('(max-width: 480px)');

  // Use global context for progress tracking
  const {
    progressData,
    isPolling,
    error,
    isCompleted,
    isFailed,
    isSkipped,
    isRunning,
    currentStep,
    allSteps,
    documentsInfo,
    performanceInfo,
    cancelImport,
    retryCount,
    activeJobId,
    startTracking
  } = useKGImportProgressContext();

  // Auto-compact mode for small screens
  useEffect(() => {
    if (isSmallMobile && !isCompact) {
      setIsCompact(true);
    } else if (!isSmallMobile && isCompact) {
      setIsCompact(false);
    }
  }, [isSmallMobile, isCompact]);

  // Auto-start progress tracking when component mounts with jobId
  useEffect(() => {
    if (jobId && !isPolling && activeJobId !== jobId) {
      if (process.env.NODE_ENV === 'development') {
        console.log('KGImportProgressTracker: Auto-starting progress tracking for job:', jobId);
      }

      startTracking(jobId, {
        pollInterval: 3000,
        maxRetries: 3,
        notificationEnabled: false,
        stickyNotifications: false
      });
    }
  }, [jobId, isPolling, activeJobId, startTracking]);

  // Handle cancel with loading state
  const handleCancel = useCallback(async () => {
    const success = await cancelImport();
    if (!success) {
      console.error(t('content.knowledgeGraph.progressTracker.cancelImportFailed'));
    }
  }, [cancelImport, t]);

  // Get current step information (memoized to prevent recalculation)
  const getCurrentStepInfo = useMemo(() => {
    if (!progressData || !progressData.current_step) return null;

    const currentStepId = progressData.current_step.id;

    // Exact match only - no backward compatibility
    return IMPORT_STEPS.find((step) => step.id === currentStepId) || null;
  }, [progressData]);

  const currentStepInfo = getCurrentStepInfo;

  // Compute effective step statuses - infer completed/running for sequential workflow.
  // Backend may skip steps or not report all previous steps, so we infer:
  // 1. Steps before the furthest active step → completed
  // 2. First pending step after completed ones → running (if job is active)
  const effectiveSteps = useMemo(() => {
    const jobIsActive = progressData?.status === 'running' || progressData?.status === 'pending';

    // Find the furthest step index that's running or completed (from backend data or currentStep)
    let furthestActiveIndex = -1;

    IMPORT_STEPS.forEach((step, index) => {
      const stepData = allSteps?.find((s) => s.id === step.id);
      if (stepData && (stepData.status === 'running' || stepData.status === 'completed')) {
        furthestActiveIndex = Math.max(furthestActiveIndex, index);
      }
    });

    const currentStepIndex = IMPORT_STEPS.findIndex((s) => s.id === currentStep);
    if (currentStepIndex >= 0) {
      furthestActiveIndex = Math.max(furthestActiveIndex, currentStepIndex);
    }

    const steps = IMPORT_STEPS.map((step, index) => {
      const stepData = allSteps?.find((s) => s.id === step.id);

      // If backend provides explicit non-pending data, use it
      if (stepData && stepData.status !== 'pending') {
        return { progress: stepData.progress, status: stepData.status };
      }

      // If this step is before the furthest active step, mark as completed
      if (furthestActiveIndex >= 0 && index < furthestActiveIndex) {
        return { progress: 100, status: 'completed' as const };
      }

      // If this step is the current step reported by backend, mark as running
      if (step.id === currentStep) {
        return { progress: stepData?.progress ?? 0, status: 'running' as const };
      }

      // Default to pending
      return { progress: stepData?.progress ?? 0, status: (stepData?.status ?? 'pending') as ProgressStep['status'] };
    });

    // If job is active, ensure the first pending step is marked as running
    if (jobIsActive) {
      const firstPendingIndex = steps.findIndex((s) => s.status === 'pending');
      if (firstPendingIndex >= 0) {
        steps[firstPendingIndex] = { ...steps[firstPendingIndex], status: 'running' };
      }
    }

    return steps;
  }, [allSteps, currentStep, progressData?.status]);

  // Only show progress for the matching job ID
  if (activeJobId !== jobId) {
    return null;
  }

  // Loading state with better mobile UX
  if (!progressData && isPolling) {
    return (
      <div
        className={`rounded-lg border border-primary/30 bg-white shadow-lg ${isMobile ? 'p-4' : 'p-6'} ${className} dark:border-border-dark dark:bg-dark-background-secondary dark:shadow-none`}
        role='status'
        aria-live='polite'
      >
        <div className='flex items-center'>
          <LoadingSpinner size='sm' className='mr-3' />
          <div className='min-w-0 flex-1'>
            <h3 className={`font-medium text-blue-800 ${isMobile ? 'text-sm' : 'text-base'} dark:text-blue-200`}>
              {KG_PROGRESS_MESSAGES.initializing}
              {process.env.NODE_ENV === 'development' && (
                <span className='ml-2 text-xs text-blue-600 dark:text-blue-400'>({jobId.slice(0, 8)}...)</span>
              )}
            </h3>
            <p className={`mt-1 text-blue-600 ${isMobile ? 'text-xs' : 'text-sm'} dark:text-blue-300`}>
              {KG_PROGRESS_MESSAGES.loadingProgress}
            </p>
            {retryCount > 0 && (
              <div className='mt-2 flex items-center text-xs text-orange-600 dark:text-orange-400'>
                <SignalIcon className='mr-1 h-3 w-3' />
                {KG_PROGRESS_MESSAGES.retrying} ({retryCount}/3)
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  // Error state - inline (rendered inside parent modal)
  if (error || isFailed) {
    return (
      <div
        className={`rounded-lg border border-red-300 bg-white shadow-lg ${isMobile ? 'p-4' : 'p-6'} ${className} dark:border-border-dark dark:bg-dark-background-secondary dark:shadow-none`}
        role='alert'
        aria-live='assertive'
      >
        <div className='flex items-start'>
          <ExclamationTriangleIcon className={`mt-0.5 text-red-400 ${isMobile ? 'h-4 w-4' : 'h-5 w-5'}`} />
          <div className='ml-3 flex-1'>
            <h3 className={`font-medium text-red-800 ${isMobile ? 'text-sm' : 'text-base'} dark:text-red-200`}>
              {KG_PROGRESS_MESSAGES.importFailed}
            </h3>
            <p className={`mt-1 text-red-600 ${isMobile ? 'text-xs' : 'text-sm'} dark:text-red-300`}>
              {error || progressData?.error_details || progressData?.message || KG_PROGRESS_MESSAGES.failed}
            </p>
            {documentsInfo && documentsInfo.failed > 0 && (
              <p className={`mt-2 text-red-500 ${isMobile ? 'text-xs' : 'text-sm'} dark:text-red-400`}>
                {KG_METRICS_LABELS.errors}: {documentsInfo.failed} {KG_METRICS_LABELS.documents}{' '}
                {t('content.knowledgeGraph.progressTracker.documentsFailed')}
              </p>
            )}
            {retryCount > 0 && (
              <div className='mt-2 flex items-center text-xs text-orange-600 dark:text-orange-400'>
                <SignalIcon className='mr-1 h-3 w-3' />
                {t('content.knowledgeGraph.progressTracker.retryAttempt', { count: retryCount })}
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  // Skipped state (duplicate detected) with warning UI
  if (isSkipped) {
    return (
      <div
        className={`rounded-lg border border-yellow-300 bg-white shadow-lg ${isMobile ? 'p-4' : 'p-6'} ${className} dark:border-border-dark dark:bg-dark-background-secondary dark:shadow-none`}
        role='alert'
        aria-live='polite'
      >
        <div className='flex items-start'>
          <ExclamationTriangleIcon className={`mt-0.5 text-yellow-500 ${isMobile ? 'h-4 w-4' : 'h-5 w-5'}`} />
          <div className='ml-3 flex-1'>
            <h3 className={`font-medium text-yellow-800 ${isMobile ? 'text-sm' : 'text-base'} dark:text-yellow-200`}>
              {KG_PROGRESS_MESSAGES.importSkipped}
            </h3>
            <p className={`mt-1 text-yellow-600 ${isMobile ? 'text-xs' : 'text-sm'} dark:text-yellow-300`}>
              {t('content.knowledgeGraph.progressTracker.duplicateDetected')}
            </p>
            <div className='mt-4 rounded-md border border-yellow-200 bg-yellow-50 p-3 dark:border-yellow-600 dark:bg-yellow-900/20'>
              <div className='flex items-center text-yellow-800 dark:text-yellow-200'>
                <ShieldCheckIcon className={`mr-2 ${isMobile ? 'h-4 w-4' : 'h-5 w-5'}`} />
                <span className={`font-medium ${isMobile ? 'text-xs' : 'text-sm'}`}>
                  {t('content.knowledgeGraph.progressTracker.systemProtection')}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Success state - inline (rendered inside parent modal)
  if (isCompleted) {
    return (
      <div
        className={`rounded-lg border border-green-300 bg-white shadow-lg ${isMobile ? 'p-4' : 'p-6'} ${className} dark:border-border-dark dark:bg-dark-background-secondary dark:shadow-none`}
        role='status'
        aria-live='polite'
      >
        <div className='flex items-start'>
          <CheckCircleIcon className={`mt-0.5 text-green-400 ${isMobile ? 'h-4 w-4' : 'h-5 w-5'}`} />
          <div className='ml-3 flex-1'>
            <h3 className={`font-medium text-green-800 ${isMobile ? 'text-sm' : 'text-base'} dark:text-green-200`}>
              {KG_PROGRESS_MESSAGES.importCompleted}
            </h3>
            <p className={`mt-1 text-green-600 ${isMobile ? 'text-xs' : 'text-sm'} dark:text-green-300`}>{KG_PROGRESS_MESSAGES.kgImported}</p>

            {/* Success metrics grid */}
            <div className={`mt-4 grid gap-3 ${isSmallMobile ? 'grid-cols-2' : isMobile ? 'grid-cols-2' : 'grid-cols-2 md:grid-cols-4'}`}>
              {documentsInfo && (
                <div className='rounded-md border border-green-200 bg-white p-3 shadow-sm dark:border-green-600 dark:bg-dark-background-tertiary'>
                  <p className={`font-medium text-green-800 ${isMobile ? 'text-xs' : 'text-sm'} dark:text-green-200`}>
                    📄 {KG_METRICS_LABELS.documents}
                  </p>
                  <p className={`font-bold text-green-900 ${isMobile ? 'text-base' : 'text-lg'} dark:text-green-100`}>
                    {documentsInfo.processed.toLocaleString()}
                  </p>
                </div>
              )}

              {performanceInfo && performanceInfo.nodesCreated > 0 && (
                <div className='rounded-md border border-blue-200 bg-blue-50 p-3 shadow-sm dark:border-blue-600 dark:bg-blue-900/20'>
                  <p className={`font-medium text-blue-800 ${isMobile ? 'text-xs' : 'text-sm'} dark:text-blue-200`}>🔗 {KG_METRICS_LABELS.nodes}</p>
                  <p className={`font-bold text-blue-900 ${isMobile ? 'text-base' : 'text-lg'} dark:text-blue-100`}>
                    {performanceInfo.nodesCreated.toLocaleString()}
                  </p>
                </div>
              )}

              {performanceInfo && performanceInfo.relationshipsCreated > 0 && (
                <div className='rounded-md border border-purple-200 bg-purple-50 p-3 shadow-sm dark:border-purple-600 dark:bg-purple-900/20'>
                  <p className={`font-medium text-purple-800 ${isMobile ? 'text-xs' : 'text-sm'} dark:text-purple-200`}>
                    🔀 {KG_METRICS_LABELS.relationships}
                  </p>
                  <p className={`font-bold text-purple-900 ${isMobile ? 'text-base' : 'text-lg'} dark:text-purple-100`}>
                    {performanceInfo.relationshipsCreated.toLocaleString()}
                  </p>
                </div>
              )}

              {performanceInfo && (
                <div className='rounded-md border border-orange-200 bg-orange-50 p-3 shadow-sm dark:border-orange-600 dark:bg-orange-900/20'>
                  <p className={`font-medium text-orange-800 ${isMobile ? 'text-xs' : 'text-sm'} dark:text-orange-200`}>
                    ⏱️ {KG_METRICS_LABELS.time}
                  </p>
                  <p className={`font-bold text-orange-900 ${isMobile ? 'text-base' : 'text-lg'} dark:text-orange-100`}>
                    {Math.floor(performanceInfo.elapsedTime / 60)}m {Math.floor(performanceInfo.elapsedTime % 60)}s
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Active progress state - inline (rendered inside parent modal)
  return (
    <div
      className={`rounded-lg border border-primary/30 bg-white shadow-lg ${isMobile ? 'p-4' : 'p-6'} ${className} dark:border-border-dark dark:bg-dark-background-secondary dark:shadow-none`}
      role='status'
      aria-live='polite'
      aria-label={t('content.knowledgeGraph.progressTracker.ariaLabel')}
    >
      <div className={`space-y-${isMobile ? '4' : '6'}`}>
        {/* Header with responsive layout */}
        <div className={`flex items-start justify-between ${isMobile ? 'flex-col space-y-3' : 'flex-row'}`}>
          <div className='flex min-w-0 flex-1 items-center'>
            <LoadingSpinner size='sm' className='mr-3 flex-shrink-0' />
            <div className='min-w-0 flex-1'>
              <h3 className={`font-medium text-blue-800 ${isMobile ? 'text-sm' : 'text-base'} dark:text-blue-200`}>
                {t('content.knowledgeGraph.progressTracker.importRunning')}
                {performanceInfo && performanceInfo.nodesCreated > 0 && (
                  <span className='ml-2 inline-flex items-center rounded-full bg-blue-100 px-2 py-1 text-xs font-medium text-blue-800 dark:bg-blue-700 dark:text-blue-200'>
                    {performanceInfo.nodesCreated.toLocaleString()} nodes
                  </span>
                )}
              </h3>
              <p className={`mt-1 text-blue-600 ${isMobile ? 'text-xs' : 'text-sm'} truncate dark:text-blue-300`}>
                Job ID: {jobId.slice(0, 8)}... • {Math.floor((performanceInfo?.elapsedTime ?? 0) / 60)}m{' '}
                {t('content.knowledgeGraph.progressTracker.elapsedTime')}
                {performanceInfo && performanceInfo.relationshipsCreated > 0 && (
                  <span className='ml-2'>
                    • {performanceInfo.relationshipsCreated.toLocaleString()} {KG_METRICS_LABELS.relationships}
                  </span>
                )}
              </p>
              {retryCount > 0 && (
                <div className='mt-1 flex items-center text-xs text-orange-600 dark:text-orange-400'>
                  <SignalIcon className='mr-1 h-3 w-3' />
                  {KG_PROGRESS_MESSAGES.retryFailed} {retryCount}
                </div>
              )}
            </div>
          </div>

          {/* Cancel button */}
          {isRunning && (
            <Button
              variant='outline'
              size='sm'
              onClick={handleCancel}
              className={`flex-shrink-0 border-red-300 text-red-700 hover:bg-red-50 dark:border-border-dark dark:text-red-400 dark:hover:bg-dark-background-hover ${isMobile ? 'self-end' : ''}`}
              aria-label={KG_ACTIONS.cancel}
            >
              <StopIcon className={`${isMobile ? 'h-3 w-3' : 'h-4 w-4'} ${isMobile ? '' : 'mr-1'}`} />
              {!isSmallMobile && KG_ACTIONS.cancel}
            </Button>
          )}
        </div>

        {/* Compact mode toggle for mobile */}
        {isMobile && (
          <div className='flex items-center justify-between'>
            <button
              onClick={() => setShowDetails(!showDetails)}
              className='flex items-center text-xs text-blue-700 transition-colors hover:text-blue-800'
              aria-expanded={showDetails}
              aria-controls='progress-details'
            >
              {showDetails ? <EyeSlashIcon className='mr-1 h-3 w-3' /> : <EyeIcon className='mr-1 h-3 w-3' />}
              {showDetails ? t('content.knowledgeGraph.progressTracker.hideDetails') : t('content.knowledgeGraph.progressTracker.showDetails')}
            </button>
            <button
              onClick={() => setIsCompact(!isCompact)}
              className='text-xs text-blue-600 transition-colors hover:text-blue-700'
              aria-label={isCompact ? KG_PROGRESS_MESSAGES.expand : KG_PROGRESS_MESSAGES.compact}
            >
              {isCompact ? t('content.knowledgeGraph.progressTracker.expand') : t('content.knowledgeGraph.progressTracker.compact')}
            </button>
          </div>
        )}

        {/* Current step indicator */}
        {currentStepInfo && showDetails && (
          <div
            id='progress-details'
            className={`rounded-lg border border-blue-200 bg-white ${isMobile ? 'p-3' : 'p-4'} dark:border-border-dark dark:bg-dark-background-tertiary`}
          >
            <div className='flex items-start space-x-3'>
              <div className={`flex-shrink-0 text-primary ${isMobile ? 'mt-0.5' : ''}`}>
                <div className={`${isMobile ? 'h-4 w-4' : 'h-5 w-5'}`}>{currentStepInfo.icon}</div>
              </div>
              <div className='min-w-0 flex-1'>
                <h4 className={`font-medium text-gray-900 ${isMobile ? 'text-sm' : 'text-base'} dark:text-dark-text`}>
                  {t(getStepTranslation(currentStepInfo.id).name)}
                </h4>
                <p className={`mt-1 text-gray-600 ${isMobile ? 'text-xs' : 'text-sm'} dark:text-dark-text-secondary`}>
                  {t(getStepTranslation(currentStepInfo.id).desc)}
                </p>
                {progressData?.message && (
                  <p className={`mt-2 text-blue-600 ${isMobile ? 'text-xs' : 'text-sm'} dark:text-blue-400`}>{progressData.message}</p>
                )}

                {/* Progress within current step */}
                {currentStep && !isCompact && (
                  <div className='mt-3 space-y-1'>
                    <div className='flex items-center justify-between text-xs text-gray-500 dark:text-dark-text-secondary'>
                      <span>{KG_PROGRESS_MESSAGES.currentStep}</span>
                      <span className='font-mono'>{currentStep}</span>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Document processing stats grid */}
        {documentsInfo && documentsInfo.total > 0 && showDetails && !isCompact && (
          <div className={`grid gap-3 ${isSmallMobile ? 'grid-cols-1' : isMobile ? 'grid-cols-2' : 'grid-cols-2 md:grid-cols-4'}`}>
            {/* Documents processed */}
            <div className='rounded-md border border-blue-200 bg-white p-3 dark:border-blue-600 dark:bg-dark-background-tertiary'>
              <div className='flex items-center'>
                <DocumentTextIcon className={`mr-2 text-blue-600 ${isMobile ? 'h-3 w-3' : 'h-4 w-4'} dark:text-blue-400`} />
                <div className='min-w-0 flex-1'>
                  <p className={`font-medium text-gray-600 ${isMobile ? 'text-xs' : 'text-sm'} dark:text-dark-text-secondary`}>{KG_METRICS_LABELS.documents}</p>
                  <p className={`font-bold text-gray-900 ${isMobile ? 'text-sm' : 'text-base'} dark:text-dark-text`}>
                    {documentsInfo.processed.toLocaleString()}/{documentsInfo.total.toLocaleString()}
                  </p>
                </div>
              </div>
            </div>

            {/* Failed documents (if any) */}
            {documentsInfo.failed > 0 && (
              <div className='rounded-md border border-red-200 bg-red-50 p-3 dark:border-red-600 dark:bg-red-900/20'>
                <div className='flex items-center'>
                  <ExclamationTriangleIcon className={`mr-2 text-red-600 ${isMobile ? 'h-3 w-3' : 'h-4 w-4'} dark:text-red-400`} />
                  <div className='min-w-0 flex-1'>
                    <p className={`font-medium text-red-600 ${isMobile ? 'text-xs' : 'text-sm'} dark:text-red-400`}>{KG_METRICS_LABELS.errors}</p>
                    <p className={`font-bold text-red-700 ${isMobile ? 'text-sm' : 'text-base'} dark:text-red-300`}>
                      {documentsInfo.failed.toLocaleString()}
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Nodes created */}
            <div className='rounded-md border border-blue-200 bg-blue-50 p-3 dark:border-blue-600 dark:bg-blue-900/20'>
              <div className='flex items-center'>
                <SparklesIcon className={`mr-2 text-blue-600 ${isMobile ? 'h-3 w-3' : 'h-4 w-4'} dark:text-blue-400`} />
                <div className='min-w-0 flex-1'>
                  <p className={`font-medium text-blue-600 ${isMobile ? 'text-xs' : 'text-sm'} dark:text-blue-400`}>🔗 {KG_METRICS_LABELS.nodes}</p>
                  <p className={`font-bold text-blue-700 ${isMobile ? 'text-sm' : 'text-base'} dark:text-blue-300`}>
                    {performanceInfo && performanceInfo.nodesCreated > 0 ? (
                      <span className='animate-pulse'>{performanceInfo.nodesCreated.toLocaleString()}</span>
                    ) : (
                      '0'
                    )}
                  </p>
                </div>
              </div>
            </div>

            {/* Relationships created */}
            <div className='rounded-md border border-purple-200 bg-purple-50 p-3 dark:border-purple-600 dark:bg-purple-900/20'>
              <div className='flex items-center'>
                <ChartBarIcon className={`mr-2 text-purple-600 ${isMobile ? 'h-3 w-3' : 'h-4 w-4'} dark:text-purple-400`} />
                <div className='min-w-0 flex-1'>
                  <p className={`font-medium text-purple-600 ${isMobile ? 'text-xs' : 'text-sm'} dark:text-purple-400`}>
                    🔀 {KG_METRICS_LABELS.relationships}
                  </p>
                  <p className={`font-bold text-purple-700 ${isMobile ? 'text-sm' : 'text-base'} dark:text-purple-300`}>
                    {performanceInfo && performanceInfo.relationshipsCreated > 0 ? (
                      <span className='animate-pulse'>{performanceInfo.relationshipsCreated.toLocaleString()}</span>
                    ) : (
                      '0'
                    )}
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Compact stats for mobile */}
        {documentsInfo && isCompact && (
          <div className='flex items-center justify-between rounded-md border border-blue-200 bg-white p-3 text-xs dark:border-border-dark dark:bg-dark-background-secondary'>
            <div className='flex items-center space-x-3'>
              <div className='flex items-center'>
                <DocumentTextIcon className='mr-1 h-3 w-3 text-blue-600 dark:text-blue-400' />
                <span className='dark:text-dark-text'>
                  {documentsInfo.processed}/{documentsInfo.total}
                </span>
              </div>
              {documentsInfo.failed > 0 && (
                <div className='flex items-center text-red-600 dark:text-red-400'>
                  <ExclamationTriangleIcon className='mr-1 h-3 w-3' />
                  <span>{documentsInfo.failed}</span>
                </div>
              )}
            </div>
            {performanceInfo && performanceInfo.elapsedTime > 0 && (
              <div className='flex items-center text-gray-500 dark:text-dark-text-secondary'>
                <ClockIcon className='mr-1 h-3 w-3' />
                <span>{Math.floor(performanceInfo.elapsedTime / 60)}m</span>
              </div>
            )}
          </div>
        )}

        {/* Step-by-step progress visualization */}
        {showDetails && !isCompact && (
          <div className={`rounded-lg border border-blue-200 bg-white ${isMobile ? 'p-3' : 'p-4'} dark:border-border-dark dark:bg-dark-background-secondary`}>
            <h4 className={`mb-3 font-medium text-gray-900 ${isMobile ? 'text-sm' : 'text-base'} dark:text-dark-text`}>
              {t('content.knowledgeGraph.progressTracker.stepsTitle')}
            </h4>
            <div className={`space-y-${isMobile ? '2' : '3'}`}>
              {IMPORT_STEPS.map((step, index) => {
                // Use pre-computed effective step data (handles sequential inference)
                const { progress: stepProgress, status: stepStatus } = effectiveSteps[index];

                // Determine step state
                const isCurrentStep = step.id === currentStep;
                const isStepCompleted = stepStatus === 'completed';
                const isStepRunning = stepStatus === 'running';
                const isStepFailed = stepStatus === 'failed';

                return (
                  <div
                    key={step.id}
                    className={`space-y-2 rounded transition-colors duration-200 ${isMobile ? 'p-2' : 'p-3'} ${
                      isCurrentStep
                        ? 'border border-blue-200 bg-blue-50 shadow-sm dark:border-blue-600 dark:bg-blue-900/20'
                        : isStepCompleted
                          ? 'border border-green-100 bg-green-50 dark:border-green-600 dark:bg-green-900/20'
                          : isStepFailed
                            ? 'border border-red-100 bg-red-50 dark:border-red-600 dark:bg-red-900/20'
                            : 'border border-gray-100 bg-gray-50 dark:border-border-dark dark:bg-dark-background-tertiary'
                    }`}
                    role='listitem'
                    aria-current={isCurrentStep ? 'step' : undefined}
                  >
                    <div className='flex items-center space-x-3'>
                      <div
                        className={`flex flex-shrink-0 items-center justify-center rounded-full transition-colors duration-200 ${
                          isMobile ? 'h-5 w-5' : 'h-6 w-6'
                        } ${isCurrentStep ? 'bg-blue-100 text-blue-600' : isStepCompleted ? 'bg-green-100 text-green-600' : isStepFailed ? 'bg-red-100 text-red-600' : 'bg-gray-100 text-gray-400'}`}
                      >
                        {isStepCompleted ? (
                          <CheckCircleIcon className={`${isMobile ? 'h-3 w-3' : 'h-4 w-4'}`} />
                        ) : isStepRunning ? (
                          <LoadingSpinner size='sm' />
                        ) : isStepFailed ? (
                          <ExclamationTriangleIcon className={`${isMobile ? 'h-3 w-3' : 'h-4 w-4'}`} />
                        ) : (
                          <ClockIcon className={`${isMobile ? 'h-3 w-3' : 'h-4 w-4'}`} />
                        )}
                      </div>

                      <div className='min-w-0 flex-1'>
                        <div className='flex items-center justify-between'>
                          <p
                            className={`truncate font-medium ${
                              isCurrentStep || isStepCompleted ? 'text-gray-900 dark:text-dark-text' : 'text-gray-500 dark:text-dark-text-secondary'
                            } ${isMobile ? 'text-xs' : 'text-sm'}`}
                          >
                            {t(getStepTranslation(step.id).name)}
                          </p>
                          <span
                            className={`ml-2 flex-shrink-0 text-xs font-medium ${
                              isStepCompleted ? 'text-green-600' : isStepRunning ? 'text-blue-600' : isStepFailed ? 'text-red-600' : 'text-gray-400'
                            }`}
                          >
                            {Math.round(stepProgress)}%
                          </span>
                        </div>

                        {/* Show description only for current step on mobile, or always on desktop */}
                        {(isCurrentStep || !isMobile) && !isCompact && (
                          <p className={`mt-1 text-gray-600 ${isMobile ? 'text-xs' : 'text-sm'} ${isMobile ? 'line-clamp-2' : ''} dark:text-dark-text-secondary`}>
                            {t(getStepTranslation(step.id).desc)}
                          </p>
                        )}

                        {/* Per-step progress bar */}
                        {(isStepRunning || isStepCompleted) && stepProgress > 0 && (
                          <div className='mt-2'>
                            <div className={`w-full rounded-full bg-gray-200 ${isMobile ? 'h-1' : 'h-1.5'} dark:bg-dark-background-tertiary`}>
                              <div
                                className={`rounded-full transition-all duration-500 ease-out ${isMobile ? 'h-1' : 'h-1.5'} ${
                                  isStepCompleted ? 'bg-green-500' : isStepFailed ? 'bg-red-500' : 'bg-blue-500'
                                }`}
                                style={{ width: `${Math.max(stepProgress, 2)}%` }}
                              />
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Compact step indicator for mobile */}
        {isCompact && (
          <div className='flex items-center justify-between rounded-md border border-blue-200 bg-white p-3 dark:border-border-dark dark:bg-dark-background-secondary'>
            <div className='flex items-center text-sm'>
              <span className='mr-2 text-gray-600 dark:text-dark-text-secondary'>{t('content.knowledgeGraph.progressTracker.compactStepLabel')}</span>
              <span className='truncate font-medium text-gray-900 dark:text-dark-text'>
                {currentStepInfo ? t(getStepTranslation(currentStepInfo.id).name) : t('content.knowledgeGraph.progressTracker.processingCompact')}
              </span>
            </div>
            <div className='flex items-center space-x-1 text-blue-600 dark:text-blue-400'>
              {IMPORT_STEPS.map((step, index) => {
                // Use pre-computed effective step data (handles sequential inference)
                const isStepCompleted = effectiveSteps[index].status === 'completed';
                const isCurrentStep = step.id === currentStep;

                return (
                  <div
                    key={step.id}
                    className={`h-1.5 w-1.5 rounded-full transition-colors duration-200 ${
                      isCurrentStep ? 'animate-pulse bg-blue-500' : isStepCompleted ? 'bg-green-500' : 'bg-gray-300'
                    }`}
                    aria-label={`Step ${index + 1} ${isStepCompleted ? 'completed' : isCurrentStep ? 'in progress' : 'pending'}`}
                  />
                );
              })}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default KGImportProgressTracker;
